import {
  capitalCase,
  useRecentInjector,
  useServerPagination,
  useToggle,
  vtrUrl,
} from '@lib/common';
import { EntityType } from '@lib/deals';
import { DataGrid, DataGridProps } from '@material-ui/data-grid';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { map } from 'rxjs/operators';
import { contentToColumns } from './columns';
import * as pintService from '@api/pint';
import * as companyService from '@api/company';
import * as personService from '@api/person';
import clsx from 'clsx';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Link,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Avatar,
  makeStyles,
  createStyles,
  Tooltip,
  TextField,
} from '@material-ui/core';
import { grey, red } from '@material-ui/core/colors';
import SearchBox from '@components/search-box';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LaunchIcon from '@material-ui/icons/Launch';
import NotInterestedRoundedIcon from '@material-ui/icons/NotInterestedRounded';
import { ColoredChip } from '@components/colored-elements';
import BackspaceIcon from '@material-ui/icons/Backspace';
import { EntityIcon } from '@components/entity-type-icon';
import { AiOutlineInfoCircle, BsPencilSquare } from '@icon/react-icons';
import InputBase from '@material-ui/core/InputBase';
import { useCompanyLoader } from 'services/company-dataloader.service';
import { Skeleton } from '@material-ui/lab';
import CategoryIcon from '@material-ui/icons/Category';
import LanguageIcon from '@material-ui/icons/Language';
import { usePersonLoader } from 'services/person-dataloader.service';
import styled from 'styled-components';
import { thumbStack } from '@lib/common/css';
import { contentDataLoader, useContentLoader } from 'services/content-dataloader.service';
import EventIcon from '@material-ui/icons/Event';
import { isValid } from 'date-fns';
import SubtitlesIcon from '@material-ui/icons/Subtitles';

interface EntitySearchModel {
  id: string;
  type: string;
  name: string;
  meta?: any;
}

interface EntitySearchProps {
  label: string;
  hideTypeSelect?: boolean;
  clearable?: boolean;
  defaultValue?: EntitySearchModel;
  onChange?: (value: EntitySearchModel) => any;
  titleEditable?: boolean;
  fixedType?: string;
  tryDefaultMetaPopulate?: boolean;
  txtMessage?: string;
}

const useEntityStyle = makeStyles(theme =>
  createStyles({
    expand: {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    disabledCard: {
      background: grey[100],
    },
    hoverEditEntity: {
      '&:hover $editEntity': {
        opacity: 1,
      },
    },
    editEntity: {
      opacity: 0,
    },
    inputBaseStyle: {
      fontSize: '1.5rem',
    },
  })
);

export function EntitySearch({
  label,
  hideTypeSelect = false,
  clearable = false,
  defaultValue = { name: '', type: EntityType.company, id: '' },
  onChange = null,
  titleEditable = false,
  fixedType = '',
  tryDefaultMetaPopulate = true,
  txtMessage = 'No Records Found',
}: EntitySearchProps) {
  const [expand, toggleExpanded] = useToggle(false);
  const [searchText, setSearchText] = useState(defaultValue.name);
  const [type, setType] = useState(defaultValue.type);
  const [entityNameEditable, toggleEntityNameEditable] = useToggle(false);
  const classes = useEntityStyle();
  const resolvedType = fixedType || type || (EntityType.company as string);

  const [searchIn, setSearchIn] = useState('name');

  const showSearchIn = resolvedType === EntityType.company || resolvedType === EntityType.person;

  const [searchTrigger, setSearchTrigger] = useState(0);

  // Entity Values
  const [entity, setEntityValue] = useState<EntitySearchModel>(defaultValue);
  const resolvedTypeValue = fixedType || entity.type || EntityType.company;
  // Form Change.
  const setPartialEntity = useRecentInjector(
    [onChange, setEntityValue, entity] as const,
    ([onChange, setEntityValue, entity], value: Partial<EntitySearchModel>) => {
      const newValue = { ...entity, ...value };
      setEntityValue(newValue);
      onChange && onChange(newValue);
    }
  );
  const entityNameEl = useRef(null);

  const typeContentPouplate =
    tryDefaultMetaPopulate && defaultValue.type === EntityType.content && defaultValue.id;

  // Try to populate content if required.
  useEffect(() => {
    if (typeContentPouplate) {
      contentDataLoader.load(defaultValue.id).then(content => setPartialEntity({ meta: content }));
      // pintService
      //   .getPintContent(defaultValue.id)
      //   .subscribe(content => setPartialEntity({ meta: content }));
    }
  }, []);

  const { resetPagination, ...paginationProps } = useServerPagination(
    ({ page, pageSize }) => {
      if (resolvedType === EntityType.company) {
        return companyService
          .filter({
            filters: [
              searchIn === 'name'
                ? {
                    id: 'vtr_company_name',
                    value: {
                      $contains: searchText,
                    },
                  }
                : {
                    id: 'vtr_id',
                    value: {
                      $eq: searchText,
                    },
                  },
            ],
            page,
            pageSize,
          })
          .pipe(
            map(res => ({
              count: res.data.count,
              data: res.data.data.map(d => ({
                ...d,
                setPartialEntity,
                id: d.vtr_id,
                name: d.vtr_company_name,
                projects_count: d.deal_content_count.content_count,
                reputation: d.deal_content_count.reputation,
                connections: d.deal_content_count.connections,
                deals: d.deal_content_count.deal_count,
                country_names: d.hqcountry,
                company_type: (d.company_types || []).map(t => t.name).join(', '),
              })),
            }))
          );
      } else if (resolvedType === EntityType.person) {
        return personService
          .personSearch({
            searchKey: (searchIn as any) || 'name',
            query: searchText,
            page,
            pageSize,
          })
          .pipe(
            map(res => ({
              ...res.data,
              data: res.data.data.map(d => ({ ...d, id: d.vtr_id, setPartialEntity })),
            }))
          );
      }

      return pintService
        .search(resolvedType.toLowerCase() as any, {
          query: searchText,
          start: pageSize * (page - 1),
          limit: pageSize,
          ...(searchText && resolvedType === EntityType.content
            ? { sort: 'asc', sort_key: 'title' }
            : {}),
        })
        .pipe(
          // Normalize to concur with server pagination params
          map(res => ({
            data: (res.data[Object.keys(res.data)[0]] as Array<any>).map(d => ({
              ...d,
              id: d.vtr_id,
              setPartialEntity,
            })),
            count: typeof res._pages === 'string' ? 0 : res._pages.total,
          }))
        );
    },
    { defPageSize: 30 },
    [resolvedType, searchTrigger]
  );

  const noId = !entity.id;
  const noName = !entity.name;
  const noType = !entity.type;
  const noValue = noId && noName;
  const showEditIcon = noId && titleEditable;
  const isTypeEditable = noId && !fixedType;

  const onTypeClick = () => {
    // Toggle Type on Click
    if (isTypeEditable) {
      setPartialEntity({
        type: resolvedTypeValue === EntityType.company ? EntityType.person : EntityType.company,
      });
    }
  };

  const clearContent = () => {
    setPartialEntity({ id: '', name: '', meta: null });
  };

  const updateEntityName = value => {
    setPartialEntity({ name: value, type: resolvedTypeValue, meta: null });
  };
  const updateType = evt => {
    const value = evt.target.value as EntityType;
    setType(() => value);
    // Side effects
    resetPagination();
  };

  //resize the inputbase to width equal the length of the value
  const reSizeInputBase = () => {
    let length = entityNameEl.current.firstChild.value.length;
    entityNameEl.current.style.width = `${length ? length * 1.2 : 16}ch`;
  };

  //After fresh load of input base resize the width equal to the length of the value
  //Use timeout since the inputbase is created asynchronosuly
  const freshLoadInputBaseToUpdateWidth = () => {
    setTimeout(() => reSizeInputBase(), 0);
  };

  return (
    <Card className={clsx(noValue && !expand && classes.disabledCard)}>
      <CardHeader
        subheader={label}
        avatar={
          <Avatar
            onClick={onTypeClick}
            style={{
              cursor: isTypeEditable && 'pointer',
            }}>
            <EntityIcon disabled={noValue} type={resolvedTypeValue} />
          </Avatar>
        }
        title={
          <>
            {entityNameEditable ? (
              <>
                <InputBase
                  autoFocus
                  placeholder="Enter an entity"
                  defaultValue={entity.name}
                  onBlur={toggleEntityNameEditable}
                  className={classes.inputBaseStyle}
                  inputProps={{ 'aria-label': 'Edit entity name' }}
                  onChange={event => {
                    updateEntityName(event.target.value);
                    reSizeInputBase();
                  }}
                  ref={entityNameEl}
                  onFocus={freshLoadInputBaseToUpdateWidth}
                />
              </>
            ) : (
              <Typography
                variant="h5"
                color={noName ? 'textSecondary' : 'inherit'}
                display="inline"
                style={{
                  marginRight: showEditIcon ? 0 : 10,
                  lineHeight: 1.334,
                  fontSize: '1.5rem',
                }}
                className={classes.hoverEditEntity}>
                {entity.name || <span className={classes.inputBaseStyle}>Select an entity</span>}
                {showEditIcon && (
                  <Tooltip title="Edit">
                    <span>
                      <BsPencilSquare
                        onClick={toggleEntityNameEditable}
                        className={classes.editEntity}
                        style={{ fontSize: '1rem', marginLeft: '2px' }}
                        color="primary"
                        fontSize="small"
                      />
                    </span>
                  </Tooltip>
                )}
              </Typography>
            )}

            <Typography
              style={{
                marginRight: 15,
                cursor: isTypeEditable && 'pointer',
                userSelect: isTypeEditable ? 'none' : 'auto',
              }}
              color="textSecondary"
              variant="body2"
              onClick={onTypeClick}
              display="inline">
              {capitalCase(resolvedTypeValue)}
            </Typography>
            {entity.id && entity.type ? (
              // <Chip
              //   size="small"
              //   component={Link}
              //   target="_blank"
              //   href={vtrUrl(entity.id, entity.type)}
              //   icon={<LaunchIcon />}
              //   label={`ID: ${entity.id}`}
              //   clickable
              //   color="primary"
              // />
              <IdentifiedEntityChips entity={entity} />
            ) : (
              <ColoredChip
                colored={noValue ? grey[600] : red[700]}
                coloredHover={noValue ? grey[600] : red[900]}
                size="small"
                icon={noValue ? <AiOutlineInfoCircle /> : <NotInterestedRoundedIcon />}
                label={noValue ? 'Empty' : 'Not Identified'}
              />
            )}
          </>
        }
        action={
          <React.Fragment>
            {clearable && !noValue && (
              <Tooltip title="Clear">
                <IconButton aria-label="remove content" onClick={clearContent}>
                  <BackspaceIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              className={clsx(classes.expand, {
                [classes.expandOpen]: expand,
              })}
              onClick={toggleExpanded}
              aria-expanded={expand}
              aria-label="show more">
              <ExpandMoreIcon />
            </IconButton>
          </React.Fragment>
        }
      />
      <Collapse in={expand} mountOnEnter unmountOnExit>
        <CardContent>
          <Grid container justify="space-between" spacing={2}>
            {!hideTypeSelect && (
              <Grid item>
                <FormControl style={{ minWidth: 100 }}>
                  <InputLabel id="entity-select-label">Entity Type</InputLabel>
                  <Select
                    labelId="entity-select-label"
                    id="entity-select"
                    value={resolvedType}
                    onChange={updateType}>
                    <MenuItem value={EntityType.company}>Company</MenuItem>
                    <MenuItem value={EntityType.person}>Person</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {showSearchIn && (
              <Grid item>
                <TextField
                  value={searchIn}
                  onChange={evt => setSearchIn(evt.target.value as string)}
                  select
                  SelectProps={{ native: true }}
                  label="Search By">
                  <option value="name">Name</option>
                  <option value="vtr_id">Vitrina Id</option>
                </TextField>
              </Grid>
            )}
            <Grid item xs>
              <SearchBox
                onSearch={text => {
                  setSearchText(text);
                  setSearchTrigger(t => t + 1);
                  resetPagination();
                }}
                defautlValue={searchText}
                label={`Search ${capitalCase(type)}`}
              />
            </Grid>
          </Grid>
          <br />
          {searchText && (
            <Typography variant="caption" color="textSecondary">
              Showing Results for <strong>"{searchText}"</strong>
            </Typography>
          )}
          <br />
          <TableContainer>
            <div style={{ width: '100%' }}>
              <EntitySearchResult
                {...paginationProps}
                density="compact"
                rowsPerPageOptions={null}
                disableColumnFilter
                disableColumnMenu
                disableColumnReorder
                disableColumnResize
                disableColumnSelector
                disableSelectionOnClick
                disableDensitySelector
                disableMultipleColumnsFiltering
                disableMultipleSelection
                disableMultipleColumnsSorting
                disableExtendRowFullWidth
                // rowHeight={100}
                // autoPageSize
                autoHeight
                type={type}
                paginationMode="server"
              />
            </div>
          </TableContainer>
        </CardContent>
      </Collapse>
    </Card>
  );
}

const TableContainer = styled.div`
  // display: flex;
  // height: 300px;
  // overflow-y: auto;
  // & * {
  //   ${thumbStack}
  // }
  .MuiDataGrid-root .MuiDataGrid-colCell,
  .MuiDataGrid-root .MuiDataGrid-cell {
    padding: 0px 8px;
  }
`;

const IdentityInformationMap = {
  [EntityType.company]: CompanyInformation,
  [EntityType.person]: PersonInformation,
  [EntityType.content]: ContentInformation,
};

function IdentifiedEntityChips({ entity }: { entity: EntitySearchModel }) {
  let Details = IdentityInformationMap[entity.type];

  return (
    <>
      <Chip
        size="small"
        component={Link}
        target="_blank"
        href={vtrUrl(entity.id, entity.type)}
        icon={<LaunchIcon />}
        label={`ID: ${entity.id}`}
        clickable
        color="primary"
      />
      {Details && (
        <>
          &nbsp;
          <Details entity={entity} />
        </>
      )}
    </>
  );
}

const loadingSkeleton = <Skeleton animation="wave" width="30%" />;

function ContentInformation({ entity }: { entity: EntitySearchModel }) {
  const { loading, data: content } = useContentLoader(entity.id);

  const release_date = !loading && content && new Date(content.release_date);

  const year = !loading && content && isValid(release_date) ? release_date.getFullYear() : 'NA';

  return !loading && content ? (
    <ContentInfo
      type={content.content_type}
      language={content.lang_names}
      year={year}
      original_title={content.original_title == content.title ? null : content.original_title}
    />
  ) : (
    loadingSkeleton
  );
}

function CompanyInformation({ entity }: { entity: EntitySearchModel }) {
  const { loading, data: company } = useCompanyLoader(entity.id);

  return !loading && company ? (
    <CompanyInfo type={company.company_types[0]?.name} hq={company.hqcountry} />
  ) : (
    loadingSkeleton
  );
}

function PersonInformation({ entity }: { entity: EntitySearchModel }) {
  const { loading, data: person } = usePersonLoader(entity.id);

  return !loading && person ? <PersonInfo role={person.role_id} /> : loadingSkeleton;
}

const PersonInfo = ({ role }) => (
  <Typography
    variant="body2"
    color="textSecondary"
    style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <LanguageIcon style={{ fontSize: '1em' }} /> {role}
  </Typography>
);

const CompanyInfo = ({ type, hq }) => (
  <Typography
    variant="body2"
    color="textSecondary"
    style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <LanguageIcon style={{ fontSize: '1em' }} /> {hq}, <CategoryIcon style={{ fontSize: '1em' }} />{' '}
    {type}.
  </Typography>
);

const ContentInfo = ({ type, language, year, original_title }) => {
  const original_title_nullified = original_title && (
    <>
      <SubtitlesIcon style={{ fontSize: '1em' }} /> {original_title}
    </>
  );
  return (
    <Typography
      variant="body2"
      color="textSecondary"
      style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <CategoryIcon style={{ fontSize: '1em' }} /> {type},{' '}
      <LanguageIcon style={{ fontSize: '1em' }} /> {language},
      <EventIcon style={{ fontSize: '1em' }} /> {year}
      {original_title_nullified}
    </Typography>
  );
};

type EntitySearchResultProps = Omit<DataGridProps, 'columns'> & {
  type: EntityType | `${EntityType}` | string;
};

/**
 * Render a table that remounts every time the type of changes.
 */
function EntitySearchResult({ type, ...props }: EntitySearchResultProps) {
  return (
    <DataGrid
      density="compact"
      key={`${type}-${props.rowCount}`}
      columns={contentToColumns[type || EntityType.company]}
      {...props}
    />
  );
}
